'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import {
  defaultLanguage,
  getLanguageFromPath,
  getLanguageFromValue,
  uiText,
  withLanguagePrefix,
  type SiteLanguage,
} from '@/data/languages';

interface NewsItem {
  id: string | number;
  title: string;
  excerpt: string;
  href: string;
  image: string;
  alt: string;
  tag: string;
  published_at?: string;
  date?: string;
}

const NewsSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<NewsItem[]>([]);
  const [language, setLanguage] = useState<SiteLanguage>(defaultLanguage);
  const t = uiText[language];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectedLanguage = getLanguageFromPath(window.location.pathname)
      ?? getLanguageFromValue(params.get('lang') || localStorage.getItem('wto_lang'));
    setLanguage(selectedLanguage);

    fetch(`/news-api.php?lang=${selectedLanguage}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Unable to load news.')))
      .then((payload) => setItems((payload.articles ?? []).slice(0, 2)))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  const withLanguage = (href: string) => {
    const [pathWithQuery, hash = ''] = href.split('#');
    const [path, query = ''] = pathWithQuery.split('?');
    const nextPath = withLanguagePrefix(path || '/', language);
    return `${nextPath}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
  };

  if (items.length === 0) return null;

  return (
    <section className="relative py-20 lg:py-24" ref={sectionRef} style={{ background: '#F4F6F9', borderTop: '1px solid rgba(15,35,64,0.08)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="reveal flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#1B6B8A' }}>
              <span className="w-6 h-px" style={{ background: '#1B6B8A' }} />
              {t.latestUpdates}
            </span>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight" style={{ color: '#0F2340' }}>
              {t.newsTitle}
            </h2>
          </div>
          <a href={withLanguage('/news-promotions/')} className="text-sm font-medium transition-colors duration-200 font-body flex items-center gap-2 self-end sm:self-auto" style={{ color: '#1B6B8A' }}>
            {t.all}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {items.map((item, i) => (
            <a key={item.id} href={withLanguage(item.href)} className={`news-card reveal group rounded-2xl overflow-hidden border ${i === 0 ? 'lg:col-span-3' : 'lg:col-span-2'}`} style={{ borderColor: 'rgba(15,35,64,0.1)', background: '#FFFFFF', boxShadow: '0 2px 16px rgba(15,35,64,0.08)', transitionDelay: `${i * 100}ms` }}>
              <div className="relative overflow-hidden" style={{ height: i === 0 ? '260px' : '220px' }}>
                <AppImage src={item.image} alt={item.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-700" sizes={i === 0 ? '(max-width: 1024px) 100vw, 60vw' : '(max-width: 1024px) 100vw, 40vw'} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium font-body backdrop-blur-sm" style={{ background: 'rgba(27,107,138,0.85)', border: '1px solid rgba(27,107,138,0.5)', color: '#FFFFFF' }}>
                    {item.tag}
                  </span>
                </div>
              </div>
              <div className="p-5 lg:p-6">
                <p className="text-xs font-body mb-2 uppercase tracking-wide" style={{ color: '#6A85A0' }}>{item.date || item.published_at}</p>
                <h3 className="font-display text-lg lg:text-xl leading-snug mb-3 transition-colors duration-300" style={{ color: '#0F2340' }}>
                  {item.title}
                </h3>
                <p className="text-sm font-body leading-relaxed line-clamp-2 mb-4" style={{ color: '#3A5070' }}>
                  {item.excerpt}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium font-body group-hover:gap-2.5 transition-all duration-200" style={{ color: '#1B6B8A' }}>
                  {t.readMore}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
